import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, IconButton, TextField, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getPendingPurchases, getPurchases, exportPurchases, importPurchases, downloadPurchasesTemplate, PaginatedPurchasesResponse } from '../api/purchases';
import { PurchaseInterface } from '../types';
import PurchasesTable from './PurchasesTable';
import PendingPurchasesTable from './PendingPurchasesTable';
import ImportErrorModal from './ImportErrorModal';

export interface PendingPurchase {
  parentName: string;
  parentPhone: string;
  related_id: string;
  amount: number;
  swimmers: string;
  variants: string;
  notes?: string;
}

function AllPurchasesPage(): React.JSX.Element {
  const [purchases, setPurchases] = useState<PurchaseInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPendingPurchases, setLoadingPendingPurchases] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingPurchases, setPendingPurchases] = useState<PendingPurchase[]>([]);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importErrorsModalOpen, setImportErrorsModalOpen] = useState(false);

  const [localFilters, setLocalFilters] = useState({
    variant_name_raw: '',
    contact_name: '',
    contact_id: '',
    status: '',
    min_amount: '',
    max_amount: '',
    start_date: '',
    end_date: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({
    variant_name_raw: '',
    contact_name: '',
    contact_id: '',
    status: '',
    min_amount: '',
    max_amount: '',
    start_date: '',
    end_date: '',
  });

  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchPendingPurchases = async () => {
    setLoading(true);
    try {
      setLoadingPendingPurchases(true)
      const data = await getPendingPurchases();
      setPendingPurchases(data.data || []);
      setLoadingPendingPurchases(false)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases(page);
    // eslint-disable-next-line
  }, [page, sortBy, sortOrder, appliedFilters]);

  useEffect(() => {
    fetchPendingPurchases();
    // eslint-disable-next-line
  }, []);

  const fetchPurchases = async (pageNum: number) => {
    setLoading(true);
    try {
      let queryParams = `page=${pageNum}&page_size=${pageSize}`;
      if (appliedFilters.variant_name_raw) {
        queryParams += `&method=search&variant_name_raw=${encodeURIComponent(appliedFilters.variant_name_raw)}`;
      }
      if (appliedFilters.contact_name) {
        queryParams += `&contact_name=${encodeURIComponent(appliedFilters.contact_name)}`;
      }
      if (appliedFilters.contact_id) queryParams += `&contact_id=${encodeURIComponent(appliedFilters.contact_id)}`;
      if (appliedFilters.status) queryParams += `&status=${encodeURIComponent(appliedFilters.status)}`;
      if (appliedFilters.min_amount) queryParams += `&min_amount=${encodeURIComponent(appliedFilters.min_amount)}`;
      if (appliedFilters.max_amount) queryParams += `&max_amount=${encodeURIComponent(appliedFilters.max_amount)}`;
      if (appliedFilters.start_date) {
        const start = new Date(appliedFilters.start_date + 'T00:00:00');
        queryParams += `&start_date=${encodeURIComponent(start.toISOString())}`;
      }
      if (appliedFilters.end_date) {
        const end = new Date(appliedFilters.end_date + 'T23:59:59.999');
        queryParams += `&end_date=${encodeURIComponent(end.toISOString())}`;
      }
      if (sortBy) {
        queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
      }
      const res: PaginatedPurchasesResponse = await getPurchases(queryParams);
      setPurchases(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
      setPage(pageNum);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleSearchClick = () => {
    setAppliedFilters(localFilters);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleExport = async () => {
    let queryParams = `page=1&page_size=${total || 100}`;
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        queryParams += `&${key}=${encodeURIComponent(value)}`;
      }
    });
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    await exportPurchases(queryParams);
  };

  const handleImport = async (data: any[]) => {
    try {
      await importPurchases(data);
      alert('הייבוא הושלם בהצלחה!');
      refreshAll();
    } catch (err: any) {
      if (err.errors) {
        setImportErrors(err.errors);
        setImportErrorsModalOpen(true);
      } else {
        alert(err.message || 'שגיאה בייבוא הנתונים');
      }
    }
  };

  const handleResetSearch = () => {
    const empty = {
      variant_name_raw: '',
      contact_name: '',
      contact_id: '',
      status: '',
      min_amount: '',
      max_amount: '',
      start_date: '',
      end_date: '',
    };
    setLocalFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const refreshAll = () => {
    fetchPurchases(page);
    fetchPendingPurchases();

  };

  const handleContactSearch = (contactId: string) => {
    const newFilters = { ...localFilters, contact_id: contactId };
    setLocalFilters(newFilters);
    setAppliedFilters(newFilters);
    setPage(1);
  };

  return (
    <Box sx={{ maxWidth: '95%', margin: '30px auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">כל הרכישות</Typography>
        <IconButton
          onClick={refreshAll}
          disabled={loading}
          title="רענן נתונים"
          sx={{ color: 'primary.main' }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <PendingPurchasesTable loading={loadingPendingPurchases} pendingPurchases={pendingPurchases} onRefresh={refreshAll} />
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>סינון רכישות</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, borderRadius: 1 }}>

            <TextField
              label="חיפוש לפי שם קורס"
              size="small"
              value={localFilters.variant_name_raw}
              onChange={(e) => setLocalFilters({ ...localFilters, variant_name_raw: e.target.value })}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="שם שחיין/הורה"
              size="small"
              value={localFilters.contact_name}
              onChange={(e) => setLocalFilters({ ...localFilters, contact_name: e.target.value })}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>סטטוס</InputLabel>
              <Select
                value={localFilters.status}
                label="סטטוס"
                onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
              >
                <MenuItem value=""><em>הכל</em></MenuItem>
                <MenuItem value="approved">מאושר</MenuItem>
                <MenuItem value="pending">ממתין</MenuItem>
                <MenuItem value="canceled">בוטל</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="חיפוש לפי Contact ID"
              size="small"
              value={localFilters.contact_id}
              onChange={(e) => setLocalFilters({ ...localFilters, contact_id: e.target.value })}
              onKeyPress={handleKeyPress}
              sx={{ width: 180 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>סכום</Typography>
            <TextField
              label="סכום מקסימלי"
              type="number"
              size="small"
              value={localFilters.max_amount}
              onChange={(e) => setLocalFilters({ ...localFilters, max_amount: e.target.value })}
              onKeyPress={handleKeyPress}
              sx={{ width: 140 }}
            />
            <TextField
              label="סכום מינימלי"
              type="number"
              size="small"
              value={localFilters.min_amount}
              onChange={(e) => setLocalFilters({ ...localFilters, min_amount: e.target.value })}
              onKeyPress={handleKeyPress}
              sx={{ width: 140 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>תאריך יצירה</Typography>
            <TextField
              label="מתאריך יצירה"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={localFilters.start_date}
              onChange={(e) => setLocalFilters({ ...localFilters, start_date: e.target.value })}
              sx={{ width: 150 }}
            />
            <TextField
              label="עד תאריך יצירה"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={localFilters.end_date}
              onChange={(e) => setLocalFilters({ ...localFilters, end_date: e.target.value })}
              sx={{ width: 150 }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleSearchClick}>סנן</Button>
          <Button variant="outlined" onClick={handleResetSearch}>נקה סינון</Button>
        </Box>
      </Paper>
      <PurchasesTable
        displayName
        purchases={purchases}
        loading={loading}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        onRefresh={refreshAll}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onExport={handleExport}
        onImport={handleImport}
        onDownloadTemplate={downloadPurchasesTemplate}
        onContactSearch={handleContactSearch}
      />
      <ImportErrorModal
        open={importErrorsModalOpen}
        onClose={() => setImportErrorsModalOpen(false)}
        errors={importErrors}
      />
    </Box>
  );
}

export default AllPurchasesPage; 